// UAT 2026-08-17 — the merged "Recommendation Approval" page (item 4/5, then merged same day).
//
// This used to be two separate pages: "Recommendations to Review" (SOW item F, decide pending
// suggestions one at a time) and this admin drill-down (item 4, browse the full cross-site decision
// history behind the acceptance percentage). Merged into one: this page now does both — decide items
// AND browse/filter the full history — so there's a single place to work from instead of two UIs
// that showed overlapping data with different scopes and could drift out of sync with each other.
//
// WHY THE PERCENTAGE MATTERS MORE THAN ANY SINGLE SUGGESTION. AMF's position is that review is
// temporary: "once we feel like the CCR has approved everything and it's working, we don't need to
// continue to have approval". The way out is the acceptance rate per reason_code — a signal running
// at 90%+ over meaningful volume has earned the right to go automatic, one signal at a time. That
// data only exists if decisions are captured from the first release, which is also why this page
// exists — see acceptanceByReason in ccrQueue.js for why the top-line rate is pooled across every
// site rather than any one bakery's own numbers.
//
// NOTHING HERE IS SENT TO A CUSTOMER. Approving/editing/rejecting only records the decision.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
	DialogTitle, IconButton, Paper, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import {
	CheckCircleOutline, EditOutlined, FileDownloadOutlined, HighlightOff, RefreshOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants";
import { toast } from "../../context/ToastContext";
import ExpandableDataGrid from "../ExpandableDataGrid/ExpandableDataGrid";
import {
	decideCcrItem, downloadWearRateCsv, generateCcrQueue, getCcrAcceptance, getCcrQueue,
	getWearRateSummary,
} from "../../services/partRecommendationService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";

const REASON_LABEL = {
	critical_not_held: "Critical spare not held",
	due_for_reorder: "Due for reorder",
	lapsed_part: "Stopped ordering",
	service_report: "Technician flagged it",
};

const STATUS_COLOR = {
	pending: { bg: "#f0f2f5", fg: "#555" },
	approved: { bg: "rgba(47,125,67,0.12)", fg: "#2f7d43" },
	edited: { bg: "rgba(47,125,67,0.12)", fg: "#2f7d43" },
	rejected: { bg: "rgba(237,31,39,0.10)", fg: "var(--primary-main)" },
};

const evidenceText = (evidence) => {
	if (!evidence || typeof evidence !== "object") return "";
	const bits = [];
	if (evidence.peer_sites_holding != null) bits.push(`held by ${evidence.peer_sites_holding} peer bakeries`);
	if (evidence.machine_name) bits.push(evidence.machine_name);
	if (evidence.last_ordered) bits.push(`last ordered ${evidence.last_ordered}`);
	if (evidence.typical_cycle_months != null) bits.push(`usual cycle ${evidence.typical_cycle_months} months`);
	if (evidence.purchases != null) bits.push(`${evidence.purchases} past purchases`);
	if (evidence.wo_number) bits.push(`work order ${evidence.wo_number}`);
	return bits.join(" · ");
};

const triggerToast = (message, severity = "success") => {
	if (!message) return;
	toast.trigger({ message, severity });
};

const RecommendationAcceptance = () => {
	const location = useLocation();
	// Deliberately read once on mount, not on every location change — this seeds the initial
	// reason/site filter from a deep link, but the filters then live in this component's own state
	// and must not snap back if the URL changes later.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const initialParams = useMemo(() => new URLSearchParams(location.search), []);
	const navigate = useNavigate();
	// Same convention as SparesInsights.js/the old Recommendations-to-Review page — opens the part
	// page when both ids resolve, falls back to a part-number search when they don't (rows with no
	// machine tie, e.g. due_for_reorder/lapsed_part).
	const openPart = (machineId, productId, productNumber) => {
		if (machineId && productId) return () => navigate(`${ROUTES.MACHINES}/${machineId}/${productId}`);
		if (productNumber) return () => navigate(`${ROUTES.SEARCH_RESULTS}?q=${encodeURIComponent(productNumber)}`);
		return null;
	};

	// Opens on Critical spare not held by default rather than a blank page — a category is always
	// selected, deep link or not.
	const [activeReason, setActiveReason] = useState(initialParams.get("reason") || "critical_not_held");
	const [siteOptions, setSiteOptions] = useState([]);
	const [selectedSite, setSelectedSite] = useState(initialParams.get("site") || null);
	// Status and free-text search now live as column-header controls on the results grid itself
	// (ExpandableDataGrid, the same component Cases/WorkOrders/Services/OrderHistory use), not a
	// separate filter row — gridSearchText mirrors the grid's own live search box value so the
	// acceptance-rate math below can keep excluding the status filter from the rate exactly as
	// before (see partFilteredRows), which the grid has no way to know on its own.
	const [gridSearchText, setGridSearchText] = useState("");
	const handleGridFilterStateChange = useCallback(({ searchText }) => {
		setGridSearchText(searchText || "");
	}, []);

	const [acceptance, setAcceptance] = useState([]);
	const [loadingSummary, setLoadingSummary] = useState(true);

	const [rows, setRows] = useState([]);
	const [queueTotal, setQueueTotal] = useState(0);
	const [queuePage, setQueuePage] = useState(0);
	const [queuePageSize] = useState(10);
	const [loadingRows, setLoadingRows] = useState(false);
	const [refreshing, setRefreshing] = useState(false);

	const [busyId, setBusyId] = useState(null);
	const [noteFor, setNoteFor] = useState(null);   // { id, action }
	const [dialogAction, setDialogAction] = useState(null);
	const [note, setNote] = useState("");

	const [wearRate, setWearRate] = useState(null);
	const [downloadingConfidence, setDownloadingConfidence] = useState(null);

	// The category percentages: global by default, but become THIS bakery's own rate once one is
	// picked — a bakery filter that only narrowed the table below while leaving the headline
	// percentage global would read as half-working.
	const loadSummary = useCallback(() => {
		setLoadingSummary(true);
		return getCcrAcceptance({ siteNumber: selectedSite || null }).then((r) => {
			setAcceptance(r?.success ? (r.by_reason || []) : []);
		}).finally(() => setLoadingSummary(false));
	}, [selectedSite]);

	useEffect(() => { loadSummary(); }, [loadSummary]);

	useEffect(() => {
		let cancelled = false;
		getAllCustomerSites().then((r) => {
			if (cancelled) return;
			const list = Array.isArray(r) ? r : (r?.data || r?.rows || []);
			setSiteOptions(list.map((s) => ({
				site_number: s.customer_site_number || s.site_number,
				label: `${s.customer_site_name || s.site_name || ""} — ${s.customer_site_number || s.site_number}`,
			})).filter((s) => s.site_number));
		}).catch(() => {});
		return () => { cancelled = true; };
	}, []);

	// Always fetches every status — the status filter below is purely a table-browsing tool and must
	// NOT change what's fetched, because the percentage is computed FROM this same set. Making the
	// fetch itself status-scoped would make the ratio circular: filter the table to "Approved" and
	// every row in view would trivially be approved, showing a meaningless 100%.
	const loadRows = useCallback(() => {
		if (!activeReason) { setRows([]); return Promise.resolve(); }
		setLoadingRows(true);
		return getCcrQueue({
			siteNumber: selectedSite || null,
			status: "all",
			reasonCode: activeReason,
			limit: queuePageSize,
			offset: queuePage * queuePageSize,
		}).then((r) => {
			setRows(r?.success ? (r.items || []) : []);
			setQueueTotal(Number(r?.total) || 0);
		})
			.finally(() => setLoadingRows(false));
	}, [activeReason, selectedSite, queuePage, queuePageSize]);

	useEffect(() => { setQueuePage(0); }, [activeReason, selectedSite]);
	useEffect(() => { loadRows(); }, [loadRows]);

	useEffect(() => {
		getWearRateSummary().then((w) => w?.success && setWearRate(w.summary)).catch(() => {});
	}, []);

	// Item K — pulls fresh suggestions for the currently-filtered bakery. Needs a specific bakery
	// (generateForSite is site-scoped), so it's disabled rather than hidden when "All bakeries" is
	// selected, with a tooltip explaining why instead of the control just disappearing.
	const refresh = async () => {
		if (!selectedSite) return;
		setRefreshing(true);
		try {
			const r = await generateCcrQueue(selectedSite);
			triggerToast(
				r?.success ? `${r.inserted} new, ${r.refreshed} updated` : "Could not refresh the queue",
				r?.success ? "success" : "error",
			);
			await Promise.all([loadRows(), loadSummary()]);
		} catch (_e) {
			triggerToast("Could not refresh the queue", "error");
		} finally {
			setRefreshing(false);
		}
	};

	const decide = async (id, action, providedNote = null) => {
		// A rejection or an edit without a note teaches the system nothing, so the API refuses a
		// rejection outright and the edit dialog exists to actually capture the reworded text — ask
		// here rather than letting a bare click hit a server error.
		if ((action === "reject" || action === "edit") && !providedNote) {
			setNote("");
			setDialogAction(action);
			setNoteFor({ id, action });
			return;
		}
		setBusyId(id);
		try {
			const r = await decideCcrItem(id, action, providedNote);
			if (r?.success) {
				triggerToast(`Marked ${r.item?.status}`, "success");
				setNoteFor(null);
				await Promise.all([loadRows(), loadSummary()]);
			} else {
				triggerToast(r?.message || "Could not record the decision", "error");
			}
		} catch (e) {
			triggerToast(e?.response?.data?.message || "Could not record the decision", "error");
		} finally {
			setBusyId(null);
		}
	};

	const handleWearRateDownload = async (confidenceLevel) => {
		setDownloadingConfidence(confidenceLevel || "all");
		try {
			const blob = await downloadWearRateCsv({ confidence: confidenceLevel || null });
			if (!(blob instanceof Blob)) {
				triggerToast("Could not download the export", "error");
				return;
			}
			// An error response is STILL a Blob when responseType is "blob" — it just holds JSON
			// rather than CSV. Without this the failure saves silently as a .csv containing
			// {"success":false,...}, which looks like a successful download until it is opened.
			if (blob.type && blob.type.includes("json")) {
				let message = "Could not download the export";
				try { message = JSON.parse(await blob.text())?.message || message; } catch (_e) { /* keep default */ }
				triggerToast(message, "error");
				return;
			}
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = confidenceLevel
				? `amf_replacement_rates_${confidenceLevel}.csv`
				: "amf_replacement_rates.csv";
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (e) {
			triggerToast("Could not download the export", "error");
		} finally {
			setDownloadingConfidence(null);
		}
	};

	// Search-filtered only (deliberately NOT status-filtered) — this is what the percentage is
	// computed from. Narrowing by search text is a legitimate way to ask "what's the acceptance
	// rate for just this subset," so it's allowed to move the number; narrowing by status is not
	// (see above) — so this deliberately excludes row.status from the matched text even though the
	// grid's own on-screen search matches every column generically. The grid itself (passed the
	// full, un-narrowed `rows`) applies both this same search text AND the status column filter to
	// decide what's actually shown in the table — this is only the parallel, status-blind version
	// used for the rate math.
	const partFilteredRows = useMemo(() => {
		const q = gridSearchText.trim().toLowerCase();
		if (!q) return rows;
		return rows.filter((r) => [
			r.customer_site_name, r.site_number, r.product_number, r.description,
			r.reason, evidenceText(r.evidence), r.decided_at,
		].some((field) => String(field || "").toLowerCase().includes(q)));
	}, [rows, gridSearchText]);

	// The ACTIVE category's own counts and percentage: bakery (via the fetch itself) + search text,
	// never status. The other, non-active chips keep the server-side summary figures, since there's
	// no table open to derive theirs from.
	const filteredCounts = useMemo(() => ({
		accepted: partFilteredRows.filter((r) => r.status === "approved" || r.status === "edited").length,
		rejected: partFilteredRows.filter((r) => r.status === "rejected").length,
		pending: partFilteredRows.filter((r) => r.status === "pending").length,
	}), [partFilteredRows]);
	const filteredAcceptancePct = useMemo(() => {
		const { accepted, rejected } = filteredCounts;
		return (accepted + rejected) === 0 ? null : Math.round((100 * accepted) / (accepted + rejected));
	}, [filteredCounts]);

	// Same shape/columns as the raw MUI table this replaced, now via the shared grid component
	// every other data table in the portal (Cases/WorkOrders/Services/OrderHistory) uses — Status
	// gets a real column-header filter (filterable: true) instead of living in a separate filter
	// row, and Part/Bakery/Reason/Evidence are covered by the grid's own built-in search box.
	const columns_ccr = [
		{
			field: "site_number", headerName: "Bakery", flex: 1.2,
			renderCell: ({ row }) => {
				const handler = openPart(row.machine_id, row.product_id, row.product_number);
				return (
					<Box onClick={handler || undefined} sx={{ fontSize: "11px", cursor: handler ? "pointer" : "default" }}>
						{row.customer_site_name ? `${row.customer_site_name} — ${row.site_number}` : row.site_number}
					</Box>
				);
			},
		},
		{
			field: "product_number", headerName: "Part", flex: 1.3,
			renderCell: ({ row }) => {
				const handler = openPart(row.machine_id, row.product_id, row.product_number);
				return (
					<Box onClick={handler || undefined} sx={{ cursor: handler ? "pointer" : "default" }}>
						<Typography sx={{ fontSize: "11px", fontWeight: 600 }}>{row.description || row.product_number}</Typography>
						<Typography sx={{ fontSize: "10px", color: "text.secondary" }}>{row.product_number}</Typography>
					</Box>
				);
			},
		},
		{ field: "reason", headerName: "Reason", flex: 1.4 },
		{
			field: "evidence", headerName: "Evidence", flex: 1.6, sortable: false,
			valueGetter: ({ row }) => evidenceText(row.evidence),
		},
		{
			field: "status", headerName: "Status", width: 130, filterable: true,
			getSearchValue: ({ row }) => row.status,
			renderCell: ({ row }) => {
				const color = STATUS_COLOR[row.status] || STATUS_COLOR.pending;
				return (
					<Chip size="small" label={row.status}
						sx={{ fontSize: "9.5px", height: 20, fontWeight: 700, backgroundColor: color.bg, color: color.fg }} />
				);
			},
		},
		{
			field: "decided_at", headerName: "Modified on", width: 130,
			// The grid tries calling this two ways (an object carrying `.value`, then the bare
			// value) — handle both explicitly rather than relying on which one happens to "win".
			valueFormatter: (input) => {
				const raw = input && typeof input === "object" && "value" in input ? input.value : input;
				return raw ? String(raw).slice(0, 10) : "—";
			},
		},
		{
			field: "actions", headerName: "Actions", width: 140, sortable: false, filterable: false,
			renderCell: ({ row }) => {
				if (row.status !== "pending") {
					return (
						<Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
							{row.decision_note || "—"}
						</Typography>
					);
				}
				return (
					<Stack direction="row" spacing={0.25}>
						<Tooltip title="Approve">
							<span>
								<IconButton size="small" color="success" disabled={busyId === row.id}
									onClick={() => decide(row.id, "approve")}>
									<CheckCircleOutline fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Edit">
							<span>
								<IconButton size="small" color="primary" disabled={busyId === row.id}
									onClick={() => decide(row.id, "edit")}>
									<EditOutlined fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Reject">
							<span>
								<IconButton size="small" sx={{ color: "var(--primary-main)" }}
									disabled={busyId === row.id}
									onClick={() => decide(row.id, "reject")}>
									<HighlightOff fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					</Stack>
				);
			},
		},
	];

	return (
		<Box>
			<Typography sx={{ fontSize: "12.5px", color: "text.secondary", mb: 1.5 }}>
				Nothing here is sent to a customer. Approving records your decision so the system can
				learn which suggestions are worth trusting.
			</Typography>

			<Typography sx={{ fontSize: "13px", fontWeight: 700, mb: 0.8 }}>
				Acceptance rate
			</Typography>

			{loadingSummary ? (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
					<CircularProgress size={18} />
					<Typography sx={{ fontSize: "13px", color: "text.secondary" }}>Loading…</Typography>
				</Box>
			) : (
				<Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 2 }}>
					{acceptance.map((a) => {
						const isActive = activeReason === a.reason_code;
						// The open category's % tracks bakery + part filters; the status filter is a
						// table-browsing tool only and never feeds the ratio (see partFilteredRows above).
						// The rest show the server-side, bakery-scoped-only summary figures.
						const pct = isActive ? filteredAcceptancePct : a.acceptance_pct;
						const counts = isActive ? filteredCounts : a;
						return (
							<Tooltip key={a.reason_code}
								title={`${counts.accepted} accepted · ${counts.rejected} rejected · ${counts.pending} pending${
									selectedSite ? ` — ${selectedSite}` : ", across every bakery"}`}>
								<Chip
									onClick={() => setActiveReason(a.reason_code)}
									color={isActive ? "primary" : "default"}
									variant={isActive ? "filled" : "outlined"}
									label={`${REASON_LABEL[a.reason_code] || a.reason_code}: ${
										pct === null ? "no decisions yet" : `${pct}%`}`}
									sx={{ fontSize: "13px", height: 32, px: 0.5, borderRadius: "6px" }} />
							</Tooltip>
						);
					})}
				</Stack>
			)}

			{/* Item K — the replacement-rate export for Bluestar. Every part with a computable cycle is
			    included, each carrying how closely customers agree, so AMF chooses the confidence floor
			    rather than having one chosen for them. */}
			{wearRate && (
				<Paper elevation={0} sx={{ p: 1.5, mb: 2, border: "1px solid #e6e8eb" }}>
					<Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 0.8 }}>
						Replacement-rate export
					</Typography>
					<Typography sx={{ fontSize: "11.5px", color: "text.secondary", mb: 1 }}>
						{wearRate.parts} parts with a computable cycle — {wearRate.high_confidence} high
						confidence, {wearRate.medium_confidence} medium, {wearRate.low_confidence} low.
						Confidence reflects how closely customers agree; a low-confidence part varies so much
						between bakeries that a single published figure would be wrong for most of them.
					</Typography>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
						<Typography sx={{ fontSize: "11.5px", fontWeight: 700, mr: 0.5 }}>
							Download:
						</Typography>
						{["high", "medium", "low", ""].map((c) => (
							<Button key={c || "all"} size="small"
								variant={c === "high" ? "contained" : "outlined"}
								className={c === "high" ? "button-primary" : ""}
								startIcon={<FileDownloadOutlined fontSize="small" />}
								disabled={downloadingConfidence !== null}
								onClick={() => handleWearRateDownload(c || null)}>
								{downloadingConfidence === (c || "all") ? "Downloading…" :
									c === "high" ? `High (${wearRate.high_confidence})`
									: c === "medium" ? `Medium (${wearRate.medium_confidence})`
									: c === "low" ? `Low (${wearRate.low_confidence})`
									: `All (${wearRate.parts})`}
							</Button>
						))}
					</Stack>
				</Paper>
			)}

			{activeReason && (
				<Paper elevation={0} sx={{ p: 1.5, border: "1px solid #e6e8eb" }}>
					{/* The bakery picker stays here rather than becoming a grid column filter: unlike
					    Status (a pure display filter over already-loaded rows), it decides what gets
					    FETCHED from the server in the first place (loadRows/loadSummary both re-query
					    scoped to selectedSite) — a column filter can only narrow rows already in hand. */}
					<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap", gap: 1.5 }}>
						<Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
							{REASON_LABEL[activeReason] || activeReason}
							{selectedSite ? ` for ${selectedSite}` : " across all bakeries"}
						</Typography>
						<Autocomplete
							size="small"
							options={siteOptions}
							value={siteOptions.find((s) => s.site_number === selectedSite) || null}
							onChange={(_e, val) => setSelectedSite(val?.site_number || null)}
							getOptionLabel={(o) => o.label || ""}
							isOptionEqualToValue={(o, v) => o.site_number === v?.site_number}
							sx={{ width: 300 }}
							renderInput={(params) => (
								<TextField {...params} label="Filter by bakery" placeholder="All bakeries" />
							)}
						/>
						<Tooltip title={selectedSite ? "" : "Pick a bakery above first — refreshing pulls new suggestions for one bakery at a time"}>
							<span>
								<Button size="small" variant="outlined" startIcon={<RefreshOutlined fontSize="small" />}
									disabled={!selectedSite || refreshing} onClick={refresh}>
									{refreshing ? "Refreshing…" : "Refresh suggestions"}
								</Button>
							</span>
						</Tooltip>
					</Stack>

					{loadingRows ? (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
							<CircularProgress size={18} />
							<Typography sx={{ fontSize: "13px", color: "text.secondary" }}>Loading…</Typography>
						</Box>
					) : rows.length === 0 ? (
						<Typography sx={{ fontSize: "12.5px", color: "text.secondary", py: 1 }}>
							{`No decisions yet for this reason${selectedSite ? " at this bakery" : ""}.`}
						</Typography>
					) : (
						<ExpandableDataGrid
							data={rows}
							columns={columns_ccr}
							rowIdField="id"
							pageSize={queuePageSize}
							serverPagination
							serverPage={queuePage}
							serverRowsPerPage={queuePageSize}
							serverRowCount={queueTotal}
							onServerPageChange={({ page }) => setQueuePage(page)}
							showSearchBar={true}
							onFilterStateChange={handleGridFilterStateChange}
						/>
					)}
				</Paper>
			)}

			{/* Labels read from dialogAction, NOT from noteFor. Cancel sets noteFor to null, and MUI keeps
			    the dialog mounted through its fade-out — deriving the wording from noteFor made a
			    rejection dialog visibly turn into the edit dialog as it closed. dialogAction holds the
			    action until the dialog is fully gone. */}
			<Dialog open={Boolean(noteFor)} onClose={() => setNoteFor(null)} fullWidth maxWidth="sm"
				TransitionProps={{ onExited: () => setDialogAction(null) }}>
				<DialogTitle sx={{ fontSize: "16px" }}>
					{dialogAction === "reject" ? "Why are you rejecting this?" : "Reword this suggestion"}
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: "12px", color: "text.secondary", mb: 1.2 }}>
						{dialogAction === "reject"
							? "Your reason is the signal — it is how the system learns which suggestions are wrong."
							: "Your wording replaces the suggested text."}
					</Typography>
					<TextField autoFocus fullWidth multiline minRows={3} value={note}
						onChange={(e) => setNote(e.target.value)} />
				</DialogContent>
				<DialogActions>
					<Button size="small" onClick={() => setNoteFor(null)}>Cancel</Button>
					<Button size="small" variant="contained" className="button-primary" disabled={!note.trim()}
						sx={{ border: "1px solid transparent", width: "auto !important" }}
						onClick={() => decide(noteFor.id, noteFor.action, note.trim())}>
						{dialogAction === "reject" ? "Reject" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default RecommendationAcceptance;
